/**
 * Playback scheduler — drives the playhead over an EDL and asks the compositor
 * to render the current frame. Frame accuracy is the compositor/decoder's job;
 * this just schedules. Renders never overlap: if one is in flight when the next
 * tick fires, the tick is skipped (the loop always targets the latest frame).
 */
import type { ClipItem, Edl } from '@repo/editor-core';
import type { Compositor } from './compositor';
import type { DecoderPool } from './decoder-pool';
import { ClipAudioPlayer, type ActiveClipAudio } from './clip-audio';
import { playSfx, preloadSfx, resumeSfx, stopSfx } from './sfx-player';

interface SfxCue {
  from: number;
  sfxId: string;
  volume: number;
}

export interface PlayerState {
  currentFrame: number;
  playing: boolean;
  durationInFrames: number;
}

export class Player {
  private compositor: Compositor;
  private pool: DecoderPool;
  private fps: number;
  private edl: Edl | null = null;

  private frame = 0;
  private playing = false;
  private rendering = false;
  private rafId: number | null = null;
  private anchorTime = 0;
  private anchorFrame = 0;

  private sfxCues: SfxCue[] = [];
  private clipAudio: ClipAudioPlayer;

  private listeners = new Set<() => void>();
  private snapshot: PlayerState = { currentFrame: 0, playing: false, durationInFrames: 0 };

  constructor(compositor: Compositor, pool: DecoderPool, fps = 30) {
    this.compositor = compositor;
    this.pool = pool;
    this.fps = fps;
    this.clipAudio = new ClipAudioPlayer((id) => this.pool.url(id));
  }

  /** The active main-track clip's audio at the current frame (null between clips). */
  private activeClipAudio(): ActiveClipAudio | null {
    if (!this.edl) return null;
    const track = this.edl.tracks.find((t) => t.kind === 'video');
    for (const it of track?.items ?? []) {
      if (
        it.type === 'clip' &&
        this.frame >= it.from &&
        this.frame < it.from + it.durationInFrames
      ) {
        return {
          assetId: it.source.assetId,
          sourceTimeSec: it.source.sourceInSec + (this.frame - it.from) / this.fps,
          volume: it.volume ?? 1,
        };
      }
    }
    return null;
  }

  /** Reconcile clip audio playback with the current frame + transport state. */
  private syncAudio(): void {
    this.clipAudio.update(this.activeClipAudio(), this.playing);
  }

  // ── External store (for React useSyncExternalStore) ──
  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };
  getSnapshot = (): PlayerState => this.snapshot;

  private emit(): void {
    this.snapshot = {
      currentFrame: this.frame,
      playing: this.playing,
      durationInFrames: this.edl?.durationInFrames ?? 0,
    };
    for (const cb of this.listeners) cb();
  }

  /** Swap the EDL (e.g. after an edit) and re-render the current frame. */
  setEdl(edl: Edl): void {
    this.edl = edl;
    /*
     * 효과음 재생. **프리뷰와 익스포트가 같은 파일을 본다** —
     * 여기(`/sfx/<file>`)와 렌더러(`SFX_BASE_URL/sfx/<file>`)가 둘 다 에디터 앱의
     * 정적 자산을 가리킨다. 예전엔 렌더러만 다른 도메인(R2)을 봐서 익스포트에 소리가
     * 안 들어갔고, 그 불일치가 이 기능을 1년간 죽였다
     * (`docs/shorts-editor-quality-redesign.md` §5 P0-3).
     */
    const sfxTrack = edl.tracks.find((t) => t.kind === 'sfx');
    this.sfxCues = (sfxTrack?.items ?? [])
      .filter((i): i is Extract<typeof i, { type: 'sfx' }> => i.type === 'sfx')
      .map((i) => ({ from: i.from, sfxId: i.sfxId, volume: i.volume ?? 1 }));
    void preloadSfx(this.sfxCues.map((c) => c.sfxId));
    // Prewarm clip audio elements too — without this the FIRST clip plays silent
    // while its <audio> lazily loads (later clips reuse the now-loaded element).
    this.clipAudio.preload(
      edl.tracks
        .filter((t) => t.kind === 'video' || t.kind === 'broll')
        .flatMap((t) => t.items)
        .filter((i): i is ClipItem => i.type === 'clip')
        .map((i) => i.source.assetId)
    );
    if (this.frame >= edl.durationInFrames) this.frame = Math.max(0, edl.durationInFrames - 1);
    this.emit();
    void this.renderCurrent();
    this.syncAudio();
  }

  /** Fire any sfx whose start frame falls in (fromFrame, toFrame]. */
  private fireSfx(fromFrame: number, toFrame: number): void {
    for (const cue of this.sfxCues) {
      if (cue.from > fromFrame && cue.from <= toFrame) playSfx(cue.sfxId, cue.volume);
    }
  }

  private async renderCurrent(): Promise<void> {
    if (!this.edl || this.rendering) return;
    this.rendering = true;
    try {
      await this.compositor.renderAt(this.edl, this.frame, this.pool);
    } catch {
      // a missing/failed asset frame shouldn't kill playback
    } finally {
      this.rendering = false;
    }
  }

  seek(frame: number): void {
    if (!this.edl) return;
    const clamped = Math.max(0, Math.min(frame, Math.max(0, this.edl.durationInFrames - 1)));
    this.frame = clamped;
    if (this.playing) {
      this.anchorTime = performance.now();
      this.anchorFrame = clamped;
    }
    this.emit();
    void this.renderCurrent();
    this.syncAudio();
  }

  play(): void {
    if (this.playing || !this.edl || this.edl.durationInFrames === 0) return;
    // Resume the shared SFX audio context on first play (user-gesture activated).
    resumeSfx();
    this.playing = true;
    this.anchorTime = performance.now();
    this.anchorFrame = this.frame;
    this.emit();
    this.syncAudio(); // start clip audio (still inside the play() user gesture)
    this.tick();
  }

  pause(): void {
    if (!this.playing) return;
    this.playing = false;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.emit();
    this.syncAudio(); // pause clip audio, park at current frame
    stopSfx(); // cut any sounding one-shot sfx — pause means silence
  }

  toggle(): void {
    this.playing ? this.pause() : this.play();
  }

  private tick = (): void => {
    if (!this.playing || !this.edl) return;
    const elapsedSec = (performance.now() - this.anchorTime) / 1000;
    const target = this.anchorFrame + Math.round(elapsedSec * this.fps);
    if (target >= this.edl.durationInFrames) {
      this.fireSfx(this.frame, this.edl.durationInFrames - 1);
      this.frame = Math.max(0, this.edl.durationInFrames - 1);
      this.pause();
      void this.renderCurrent();
      return;
    }
    if (target !== this.frame) {
      this.fireSfx(this.frame, target);
      this.frame = target;
      this.emit();
      void this.renderCurrent();
      this.syncAudio(); // advance/switch clip audio + correct drift
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  dispose(): void {
    this.pause();
    this.listeners.clear();
    this.clipAudio.dispose();
  }
}
